import { useEffect, useState } from "react";
import {
  Building2,
  Tag,
  Maximize2,
  CalendarCheck,
  Landmark,
  Bed,
} from "lucide-react";
import {
  Amenities,
  DetailItem,
  DetailRow,
  DeveloperInfo,
  ImageGallery,
  MapEmbed,
} from "../../../../components/admin/common/details-components";
import { formatPrettyAddress, formatPrice } from "../../../../utils/helpers";
import PropertyTitleCard from "./PropertyTitleCard";

const formatStatus = (status) => {
  const statusMap = {
    ready_to_move: "Ready to Move",
    under_construction: "Under Construction",
    new_launch: "New Launch",
  };
  return statusMap[status] || status || "Not Specified";
};

const formatOwnership = (ownership) => {
  const ownershipMap = {
    freehold: "Freehold",
    leasehold: "Leasehold",
    cooperative: "Cooperative Society",
    power_of_attorney: "Power of Attorney",
  };
  return ownershipMap[ownership] || ownership || "Not Specified";
};

const PropertyDetailsCard = ({ property }) => {
  const floorPlan = property?.property?.floorPlan;
  const expectedPrice = floorPlan?.expectedPrice;
  const carpetArea = floorPlan?.carpetArea;
  const allInclusivePrice = floorPlan?.conditions?.allInclusivePrice;
  const availabilityStatus = property?.property?.availabilityStatus;
  const ownership = property?.property?.ownership;
  const bhk = floorPlan?.bhk;
  const bathrooms = floorPlan?.bathrooms;
  const balcony = floorPlan?.balcony;

  return (
    <div className="w-full bg-white rounded-lg  shadow-sm p-4 sm:p-6 border border-gray-200">
      {/* Price */}
      <div className="flex items-center gap-x-4 text-gray-900 font-semibold text-lg sm:text-xl mb-3">
        <div className="flex items-center gap-1">
          <Tag className="mr-2 flex-shrink-0 w-4 h-4 scale-[-1]" />
          {formatPrice(expectedPrice)}
        </div>
        {allInclusivePrice && (
          <span className="ml-2 font-normal text-sm text-gray-500">
            All inclusive Price
          </span>
        )}
      </div>

      {/* Project Status & Ownership */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-3 border-y border-gray-200">
        <DetailItem
          icon={<CalendarCheck className="text-gray-400 w-5 h-5" />}
          label="Project Status"
          value={formatStatus(availabilityStatus)}
          borderRight
        />
        <DetailItem
          icon={<Landmark className="text-gray-400 w-5 h-5" />}
          label="Ownership"
          value={formatOwnership(ownership)}
        />
      </div>

      {/* Configuration & Size */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-3 border-b border-gray-200">
        {bhk && (
          <div className="sm:col-span-2">
            <DetailItem
              icon={<Bed className="text-gray-400 w-5 h-5" />}
              label="Configuration"
              value={`${bhk} BHK${bathrooms ? ` | ${bathrooms} Bath` : ""}${
                balcony ? ` | ${balcony} Balcony` : ""
              }`}
              borderRight
            />
          </div>
        )}
        <div className="sm:col-span-1">
          {carpetArea?.value && (
            <DetailItem
              icon={<Maximize2 className="text-gray-400 w-5 h-5" />}
              label="Carpet Area"
              value={`${carpetArea.value} ${carpetArea.unit || "sq. ft."}`}
            />
          )}
        </div>
      </div>

      {/* Availability */}
      {availabilityStatus && (
        <div className="pt-3">
          <DetailItem
            icon={<Building2 className="text-gray-400 w-5 h-5" />}
            label="Availability"
            value={formatStatus(availabilityStatus)}
          />
        </div>
      )}
    </div>
  );
};

const MoreDetailsCard = ({ property }) => {
  const floorPlan = property?.property?.floorPlan;
  const brokerage = property?.property?.brokerage;
  const priceNegotiable = floorPlan?.conditions?.priceNegotiable;

  return (
    <div className="bg-white rounded-xl  shadow-sm border border-gray-200 p-4 sm:p-6 w-full">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
        More Details
      </h3>

      <div className="space-y-3">
        <DetailRow
          label="Price"
          value={formatPrice(floorPlan?.expectedPrice)}
        />
        {floorPlan?.pricePerSqft && (
          <DetailRow
            label="Price per sq.ft."
            value={formatPrice(floorPlan.pricePerSqft)}
          />
        )}

        {brokerage && <DetailRow label="Brokerage" value={`${brokerage}%`} />}
        <DetailRow
          label="Availability"
          value={formatStatus(property?.property?.availabilityStatus)}
        />
        <DetailRow
          label="Ownership"
          value={formatOwnership(property?.property?.ownership)}
        />
        {priceNegotiable !== undefined && (
          <DetailRow
            label="Price Negotiable"
            value={priceNegotiable ? "Yes" : "No"}
          />
        )}
        {property?.location && (
          <DetailRow
            label="Address"
            value={property?.location?.address?.formatted}
          />
        )}
      </div>
    </div>
  );
};

const AboutProperty = ({ property }) => {
  const projectName = property?.property?.projectName;
  const description = property?.description;
  const whyToChoose = property?.property?.whyToChooose;

  if (!projectName && !description) return null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl  shadow-sm p-5 sm:p-6 mx-auto w-full border">
        <h2 className="font-medium text-gray-900 tracking-[0.01em]   text-xl sm:text-2xl  mb-3">
          About {projectName || "Property"}
        </h2>

        <div className="border-t-2 border-gray-200 mb-4"></div>

        <div className="text-sm sm:text-base md:text-[1rem] lg:text-[1.1rem] tracking-wide text-gray-600 md:text-justify w-full">
          {description && (
            <p className="text-[16px] text-gray-600 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>

      {whyToChoose && (
        <div className="bg-white rounded-xl   shadow-sm p-5 sm:p-6 mx-auto w-full border">
          <h2 className="font-medium text-gray-900 tracking-[0.01em]   text-xl sm:text-2xl  mb-3">
            Why to choose us?
          </h2>

          <div className="border-t-2 border-gray-200 mb-4"></div>

          <div className="space-y-3 text-sm sm:text-base md:text-[1rem] lg:text-[1.1rem] tracking-wide text-gray-600 md:text-justify w-full">
            <p className="text-[16px] text-gray-600 leading-relaxed">
              {whyToChoose}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminResidentialPropertyDetails = (prop) => {
  // console.log("property :", prop.property);
  const [property, setProperty] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setProperty(prop?.property);
  }, [prop?.property]);

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading property details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <main className="max-w-full mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-4">
            <ImageGallery images={property.images} title={property.title} />
            <AboutProperty property={property} />
            <DeveloperInfo property={property} />
            <Amenities amenities={property?.property?.amenities || []} />
          </div>

          {/* Right Column - Quick Details */}
          <div className="space-y-4">
            <PropertyTitleCard property={property} />
            <PropertyDetailsCard property={property} />
            <MoreDetailsCard property={property} />
            <MapEmbed
              className={"aspect-square h-auto"}
              coordinates={property?.location?.coordinates}
              address={formatPrettyAddress(property?.location)}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminResidentialPropertyDetails;
